import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  IAuditoriaArredondamentosAjustesResultado,
  IItemArredondamentosAjustes,
} from '../interfaces/IAuditoriaArredondamentosAjustes';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaArredondamentosAjustesResultado;
}

// COR-241: rótulo legível do subtipo — usado no badge da tabela e na coluna "Subtipo" da
// exportação. "Ajuste de Exercício Anterior Material - Requer Validação" é o rótulo pedido
// literalmente no ticket para o novo subtipo EXERCICIO_ANTERIOR_MATERIAL.
const labelSubtipoAjuste = (subtipo: string): string => {
  switch (subtipo) {
    case 'AJUSTE_MATERIAL':
      return 'Ajuste Manual Material';
    case 'EXERCICIO_ANTERIOR':
      return 'Ajuste de Exercício Anterior';
    case 'EXERCICIO_ANTERIOR_MATERIAL':
      return 'Ajuste de Exercício Anterior Material - Requer Validação';
    case 'FOLHA_FERIAS':
      return 'Arredondamento Operacional (Folha/Férias)';
    case 'ARREDONDAMENTO_BAIXO_VALOR':
      return 'Arredondamento Operacional (Baixo Valor)';
    case 'RECLASSIFICACAO':
      return 'Reclassificação Contábil';
    case 'HISTORICO_INSUFICIENTE':
      return 'Histórico Insuficiente';
    default:
      return subtipo;
  }
};

// COR-241: colunas da exportação — pedidas explicitamente no ticket: conta, data, histórico,
// valor, lançamento ECD, contrapartida, subtipo, status e justificativa.
interface LinhaExportacaoAjuste {
  Empresa: string;
  'Conta Contábil': string;
  'Descrição da Conta': string;
  Data: string;
  'Histórico': string;
  'Valor (R$)': string;
  'Lançamento ECD': string;
  Contrapartida: string;
  Subtipo: string;
  Status: string;
  'Multa ECD Estimada (R$)': string;
  Justificativa: string;
}

const montarLinhasExportacaoAjustes = (itens: IItemArredondamentosAjustes[]): LinhaExportacaoAjuste[] =>
  itens.map(item => ({
    Empresa: item.empresaNome,
    'Conta Contábil': item.contaContabil,
    'Descrição da Conta': item.descricaoConta,
    Data: formatDate(item.dataLancamento),
    'Histórico': item.historico,
    'Valor (R$)': item.valorBase.toFixed(2).replace('.', ','),
    'Lançamento ECD': item.numeroLancamento || '',
    Contrapartida: item.contasAgrupadas || '',
    Subtipo: labelSubtipoAjuste(item.subtipoAjuste),
    Status: item.status,
    'Multa ECD Estimada (R$)': item.impactoFiscalTotal.toFixed(2).replace('.', ','),
    Justificativa: item.mensagemDiagnostico,
  }));

const exportarCSVAjustes = (linhas: LinhaExportacaoAjuste[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const cabecalho = Object.keys(linhas[0]);
  const corpo = linhas.map(linha => Object.values(linha));
  const csv = [cabecalho, ...corpo]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${nomeArquivo}.csv`);
};

const exportarXLSXAjustes = (linhas: LinhaExportacaoAjuste[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(linhas);
  const wb = { Sheets: { Ajustes: ws }, SheetNames: ['Ajustes'] };
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, `${nomeArquivo}.xlsx`);
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaArredondamentosAjustesDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const lancamentosAjuste = resultado.lancamentosAjuste || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  // COR-241: nome do arquivo derivado do próprio item (empresaNome já vem em cada ocorrência).
  const nomeBaseAjustes = `ajustes_arredondamentos_${(lancamentosAjuste[0]?.empresaNome || 'empresa').replace(/[^\w-]+/g, '_')}`;

  const exportarAjustes = (formato: 'csv' | 'xlsx', filtroStatus?: string) => {
    const itens = filtroStatus ? lancamentosAjuste.filter(i => i.status === filtroStatus) : lancamentosAjuste;
    const linhas = montarLinhasExportacaoAjustes(itens);
    const sufixo = filtroStatus ? `_${filtroStatus.toLowerCase()}` : '_tudo';
    if (formato === 'csv') exportarCSVAjustes(linhas, `${nomeBaseAjustes}${sufixo}`);
    else exportarXLSXAjustes(linhas, `${nomeBaseAjustes}${sufixo}`);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Arredondamentos e Lançamentos de Ajustes — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        {/* COR-228: renomeado de "Impacto Fiscal Estimado" — este módulo nunca calcula tributo,
            só a multa acessória de inexatidão de escrituração (Art. 12, II, Lei 8.218/1991).
            "Impacto Fiscal" fica reservado para módulos com estimativa de tributo/omissão de
            receita/crédito tributário direto. */}
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa ECD Estimada</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>
              Multa ECD 5% por inexatidão de escrituração, limitada a 1% da receita bruta do período
              (Art. 12, II, Lei 8.218/1991) — não é tributo devido. Valor estimado, sujeito a
              confirmação documental de cada lançamento.
            </div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / com Ajuste</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-alerta'>{lancamentosAjuste.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos manuais analisados no período (exclui saldos/encerramento automáticos).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada aos lançamentos de ajuste.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Lançamentos com Histórico de Ajuste/Arredondamento</h3>
        <p className='text-muted'>
          Lançamentos (Ativo, Passivo e Resultado) cujo histórico cita "ajuste" ou "arredondamento" — risco de
          inexatidão de escrituração no SPED Contábil (Art. 12, II, Lei 8.218/1991), não presunção de omissão de
          receita. Multa acessória de escrituração, distinta de tributo devido.
        </p>

        {lancamentosAjuste.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento com histórico citando ajuste ou arredondamento identificado no período.</div>
        )}

        {/* COR-241: exportação — conta, data, histórico, valor, lançamento ECD, contrapartida,
            subtipo, status e justificativa, pedidos explicitamente no ticket. */}
        {lancamentosAjuste.length > 0 && (
          <div className='d-flex flex-wrap gap-2 align-items-center mt-3 mb-1'>
            <strong style={{ marginRight: 4, fontSize: '0.85rem' }}>
              <i className='bi bi-download me-1' />
              Exportar lançamentos:
            </strong>
            <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportarAjustes('csv')}>
              Tudo (CSV) — {lancamentosAjuste.length}
            </button>
            <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportarAjustes('xlsx')}>
              Tudo (XLSX)
            </button>
            {resultado.totalAlertas > 0 && (
              <button type='button' className='btn btn-sm btn-outline-warning' onClick={() => exportarAjustes('csv', 'ALERTA')}>
                Alertas (CSV) — {resultado.totalAlertas}
              </button>
            )}
            {resultado.totalAvisos > 0 && (
              <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => exportarAjustes('csv', 'AVISO')}>
                Avisos (CSV) — {resultado.totalAvisos}
              </button>
            )}
          </div>
        )}

        {lancamentosAjuste.length > 0 && (
          <div className='table-responsive mt-2'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data</th>
                  <th>Histórico</th>
                  <th>Subtipo</th>
                  <th className='text-end'>Valor Base</th>
                  <th className='text-end'>Multa ECD (5%, lim. 1% RB)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosAjuste.map((item: IItemArredondamentosAjustes, idx) => {
                  const chave = `aa-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td style={{ maxWidth: 280 }}>{item.historico}</td>
                        <td style={{ maxWidth: 220, fontSize: '0.78rem' }}>{labelSubtipoAjuste(item.subtipoAjuste)}</td>
                        <td className='text-end'>{formatCurrency(item.valorBase)}</td>
                        <td className='text-end auditoria-col-highlight'>{formatCurrency(item.impactoFiscalTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Alerta:</strong> Risco de Não Conciliação Identificado.</div>
                              <div><strong>Subtipo:</strong> {labelSubtipoAjuste(item.subtipoAjuste)}.</div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              {item.numeroLancamento && (
                                <div><strong>Lançamento ECD:</strong> {item.numeroLancamento}.</div>
                              )}
                              {item.contasAgrupadas && (
                                <div><strong>Contrapartida:</strong> {item.contasAgrupadas}.</div>
                              )}
                              <div><strong>Multa ECD (5%, lim. 1% RB):</strong> {formatCurrency(item.impactoFiscalTotal)}.</div>
                              <div><strong>Contexto da Conta no Período (I150/I155):</strong> Saldo inicial {formatCurrency(item.saldoInicialConta)} |
                                {' '}Saldo final {formatCurrency(item.saldoFinalConta)} | Débito {formatCurrency(item.totalDebitoConta)} |
                                {' '}Crédito {formatCurrency(item.totalCreditoConta)}</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaArredondamentosAjustesDetalhe;
